import { HoverPopover } from '@app/components/base/HoverPopover';
import { usePlaybackSeekInRange } from '@app/core/playback/hooks/usePlayback';
import { PersonAppearance, PersonLane } from '@app/modules/persons/appearances/types';
import { StreamEl } from '@mi-front/ui';
import { Box } from '@mui/material';
import { useSetCursor } from '../../../../stores/stream-screen.store';
import { applyLaneHover, HoverLaneSetter } from '../shared/apply-lane-hover';
import { PersonInfo } from './PersonInfo';
import { personsTrackPopoverProps } from '../shared/persons-track-popover-props';
import {
  appearanceBarEmphasisSx,
  appearanceBarSx,
  MIN_APPEARANCE_BAR_WIDTH_PX,
} from '../shared/persons-track-visual-tokens';
import { usePersonsTrackCapture } from '../hooks/usePersonsTrackCapture';

type AppearanceBarProps = {
  lane: PersonLane;
  appearance: PersonAppearance;
  /** Bar height in px (lane vs summary strip). */
  height: number;
  /** Vertical offset inside the Stream row. */
  offsetY: number;
  dimmed?: boolean;
  emphasized?: boolean;
  onHoverLane?: HoverLaneSetter;
};

/**
 * Interactive detection interval on the Personas track timeline.
 *
 * Shows a solid (face) or hatched (voice) bar spanning `appearance.from`–`to`,
 * colored by the person's lane. Click seeks playback there; hover opens
 * {@link PersonInfo} and can highlight the person across the track.
 * Used by both the summary strip and per-person swim lanes.
 */
export function AppearanceBar({
  lane,
  appearance,
  height,
  offsetY,
  dimmed = false,
  emphasized = false,
  onHoverLane,
}: AppearanceBarProps) {
  const setCursor = useSetCursor();
  const seekInRange = usePlaybackSeekInRange();
  const { appearanceBarClicked } = usePersonsTrackCapture();

  const goTo = () => {
    appearanceBarClicked(lane, appearance);
    setCursor(appearance.from);
    seekInRange(appearance.from);
  };

  return (
    <StreamEl
      ts={appearance.from}
      position="top"
      offsetY={offsetY}
      // HoverPopover wraps the trigger in an inline-flex span; without lineHeight 0 the
      // line box pushes the bar ~8px down and the voice bar crosses the row border.
      style={{ lineHeight: 0 }}
    >
      {(scale) => {
        const width = Math.max(
          (scale(appearance.to) ?? 0) - (scale(appearance.from) ?? 0),
          MIN_APPEARANCE_BAR_WIDTH_PX,
        );
        return (
          <HoverPopover
            trigger={
              <Box
                onClick={(e) => {
                  e.stopPropagation();
                  goTo();
                }}
                sx={{
                  width,
                  height,
                  ...appearanceBarSx(lane.color, appearance.source),
                  ...appearanceBarEmphasisSx({
                    dimmed,
                    emphasized,
                    color: lane.color,
                  }),
                }}
              />
            }
            {...personsTrackPopoverProps}
            onHoverChange={
              onHoverLane
                ? (hovered) => applyLaneHover(onHoverLane, lane.laneKey, hovered)
                : undefined
            }
          >
            <PersonInfo lane={lane} hovered={appearance} />
          </HoverPopover>
        );
      }}
    </StreamEl>
  );
}
